import React from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Alert,
  Stack,
  useTheme,
  useMediaQuery,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { Card } from '../../UI/Card';
import { Button } from '../../UI/Button';
import { ProfileFormProps } from './types';
import { disabledTextFieldStyles } from './utils';

// フィールドエラー型定義
interface FieldErrors {
  fullName?: string;
  furigana?: string;
  phone?: string;
  organization?: string;
  bio?: string;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({
  formData,
  isEditing,
  isUpdating,
  error,
  onInputChange,
  onSave,
  onCancel,
  onEdit,
  onClearError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});

  // バリデーション関数
  const validateField = (field: keyof FieldErrors, value: string): string | undefined => {
    switch (field) {
      case 'fullName':
        if (!value.trim()) return '氏名を入力してください';
        if (value.length > 100) return '氏名は100文字以内で入力してください';
        break;
      case 'furigana':
        if (value && !/^[ぁ-んー\s　]*$/.test(value)) return 'ひらがなで入力してください';
        if (value && value.length > 100) return 'ふりがなは100文字以内で入力してください';
        break;
      case 'phone':
        if (value && !/^[\d\-\+\(\)\s]*$/.test(value)) return '有効な電話番号を入力してください';
        if (value && value.length > 20) return '電話番号は20文字以内で入力してください';
        break;
      case 'organization':
        if (value && value.length > 100) return '組織名は100文字以内で入力してください';
        break;
      case 'bio':
        if (value && value.length > 500) return '自己紹介は500文字以内で入力してください';
        break;
    }
    return undefined;
  };

  // 全フィールドのバリデーション
  const validateAllFields = (): FieldErrors => {
    const errors: FieldErrors = {};
    
    const fullNameError = validateField('fullName', formData.fullName || '');
    if (fullNameError) errors.fullName = fullNameError;

    const furiganaError = validateField('furigana', formData.furigana || '');
    if (furiganaError) errors.furigana = furiganaError;

    const phoneError = validateField('phone', formData.phone || '');
    if (phoneError) errors.phone = phoneError;
    
    const organizationError = validateField('organization', formData.organization || '');
    if (organizationError) errors.organization = organizationError;
    
    const bioError = validateField('bio', formData.bio || '');
    if (bioError) errors.bio = bioError;
    
    return errors;
  };

  // フィールド変更時のバリデーション
  const validateAndUpdateField = (field: keyof FieldErrors, value: string) => {
    const fieldError = validateField(field, value);
    setFieldErrors(prev => ({
      ...prev,
      [field]: fieldError,
    }));
  };

  // 入力変更ハンドラー（リアルタイムバリデーション付き）
  const handleInputChange = (field: keyof FieldErrors) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    
    // リアルタイムバリデーション
    validateAndUpdateField(field, value);
    
    // 親コンポーネントの処理を呼び出し
    onInputChange(field as any)(event);
  };

  // 保存処理（バリデーション付き）
  const handleSave = () => {
    const errors = validateAllFields();
    setFieldErrors(errors);
    
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      // エラーがある場合は保存を阻止
      return;
    }
    
    // バリデーション成功時のみ保存実行
    onSave();
  };
  return (
    <Card
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" fontSize="large" />
          <Typography 
            variant="h5" 
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
          >
            プロフィール詳細
          </Typography>
        </Box>
      }
      action={
        !isEditing ? (
          <Button
            variant="outline"
            size={isMobile ? "small" : "medium"}
            startIcon={<EditIcon />}
            onClick={onEdit}
          >
            編集
          </Button>
        ) : (
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={1}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              variant="primary"
              size={isMobile ? "small" : "medium"}
              startIcon={<SaveIcon />}
              onClick={handleSave}
              loading={isUpdating}
              disabled={isUpdating}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              保存
            </Button>
            <Button
              variant="outline"
              size={isMobile ? "small" : "medium"}
              startIcon={<CancelIcon />}
              onClick={onCancel}
              disabled={isUpdating}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              キャンセル
            </Button>
          </Stack>
        )
      }
      padding="large"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={onClearError}>
          {error}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="氏名"
            value={formData.fullName}
            onChange={handleInputChange('fullName')}
            disabled={!isEditing || isUpdating}
            required
            error={!!fieldErrors.fullName}
            helperText={fieldErrors.fullName || '必須項目です'}
            InputProps={{
              startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{
              ...disabledTextFieldStyles,
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
            }}
            size={isMobile ? 'small' : 'medium'}
          />
        </Grid>

        {/* ふりがな */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="ふりがな"
            value={formData.furigana ?? ''}
            onChange={handleInputChange('furigana')}
            disabled={!isEditing || isUpdating}
            placeholder="さとう たろう"
            error={!!fieldErrors.furigana}
            helperText={fieldErrors.furigana || 'ひらがなで入力してください（任意）'}
            InputProps={{
              startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{
              ...disabledTextFieldStyles,
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
            }}
            size={isMobile ? 'small' : 'medium'}
          />
        </Grid>

        {/* 性別 */}
        <Grid item xs={12}>
          <FormControl disabled={!isEditing || isUpdating}>
            <FormLabel sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, mb: 0.5 }}>性別（任意）</FormLabel>
            <RadioGroup
              row
              value={formData.gender ?? ''}
              onChange={(e) => {
                const syntheticEvent = { target: { value: e.target.value } } as React.ChangeEvent<HTMLInputElement>;
                onInputChange('gender')(syntheticEvent);
              }}
            >
              <FormControlLabel value="male"   control={<Radio size="small" />} label="男性" />
              <FormControlLabel value="female" control={<Radio size="small" />} label="女性" />
              <FormControlLabel value="other"  control={<Radio size="small" />} label="その他" />
              {formData.gender && (
                <FormControlLabel
                  value=""
                  control={<Radio size="small" />}
                  label="選択解除"
                  sx={{ color: 'text.secondary' }}
                />
              )}
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="電話番号"
            value={formData.phone}
            onChange={handleInputChange('phone')}
            disabled={!isEditing || isUpdating}
            placeholder="090-1234-5678"
            error={!!fieldErrors.phone}
            helperText={fieldErrors.phone || '数字、ハイフン、括弧、スペースのみ使用可能'}
            InputProps={{
              startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{
              ...disabledTextFieldStyles,
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
            }}
            size={isMobile ? 'small' : 'medium'}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="組織・会社名"
            value={formData.organization}
            onChange={handleInputChange('organization')}
            disabled={!isEditing || isUpdating}
            placeholder="株式会社○○○"
            error={!!fieldErrors.organization}
            helperText={fieldErrors.organization || '任意項目'}
            InputProps={{
              startAdornment: <BusinessIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{
              ...disabledTextFieldStyles,
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
            }}
            size={isMobile ? 'small' : 'medium'}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="自己紹介"
            value={formData.bio}
            onChange={handleInputChange('bio')}
            disabled={!isEditing || isUpdating}
            multiline
            rows={isMobile ? 3 : 4}
            placeholder="ご自身について簡単にご紹介ください..."
            error={!!fieldErrors.bio}
            helperText={fieldErrors.bio || `${formData.bio?.length || 0}/500文字`}
            inputProps={{ maxLength: 500 }}
            sx={{
              ...disabledTextFieldStyles,
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
            }}
            size={isMobile ? 'small' : 'medium'}
          />
        </Grid>
      </Grid>

    </Card>
  );
}; 